/*
    This file handles storing user details and refreshing tokens.

    This should be the only file that uses the setters in src/api/store.js.
*/

import { clearLocalStorage, getLoginDetailsFromLocalStorage, updateLocalStorageOnLogin, updateLocalStorageOnRefreshToken, updateNativeLayerOnRefreshToken } from "../../utils/manageStorage";
import { isIos, isMobileApp } from "../../utils/getHost";

import { logForcedLogout, logLogin } from "../athena";
import { login, refreshToken, refreshTokenAndGetAccountData, logout as logoutAPI, loginWithDemoAccount, ssoLogin } from "../user";

import { getAuthenticatorForRegion } from "./servers";
import { setRefreshTokensIfStaleBeforePrivateRequest } from "./requestBuilder";
import { clearDetailsAndTokens, updateDetails, getRegion, updateTokens } from "./store";

// Tokens last 20 mins; we refresh after 10 mins (8 mins for mobile due to backgrounding)
const REFRESH_TOKENS_TIMEOUT = isMobileApp ? 480000 : 600000;

// Key info
const state = {
    // Date object representing when auth tokens were last updated
    // All tokens are refreshed together so we don't need a separate timestamp for management token
    lastAuthTokenUpdate: null,
    // Whenever auth token is updated, we create a new timeout to refresh it
    // This timeout is stored here so it can be cleared if necessary
    authTokenRefreshTimeout: null,
    // Shared promise for in-flight token refreshes
    authTokenRefreshPromise: null,
    // Callback invoked if refreshing token fails
    onInvalidToken: null,
    // Callback invoked if network error prevents token refresh
    onNetworkError: null,
    // Whether to store login token and series id in local storage
    // This will be true unless using demo
    updateLocalStorage: false,
    // If `updateLocalStorage` is false, then login token and series id are stored here instead
    webLogin: null
};

// Clears info above and any stored details/tokens
// Called when user is logged out
const resetState = () => {
    clearDetailsAndTokens();
    state.lastAuthTokenUpdate = null;
    state.authTokenRefreshTimeout = null;
    state.authTokenRefreshPromise = null;
    state.onInvalidToken = null;
    state.onNetworkError = null;
    state.updateLocalStorage = false;
    state.webLogin = null;
}

// Use these functions to log a user in, either through email/password or login token in local storage
// onInvalidToken is called when a token refresh fails
// Returns account data
/**
 * Wrapper for `initRequestManager` function that uses the given email/password. Will store web login details in local storage.
 * @param {string} email Email to log in with.
 * @param {string} password Password to log in with.
 * @param {Function} onInvalidToken Invoked if a token refresh fails.
 * @param {Function} onNetworkError Invoked if token cannot be refreshed due to network error.
 * @returns Account data
 */
export const initRequestManagerEmailPassword = async (email, password, onInvalidToken, onNetworkError) => {
    const { webLogin, ...data } = await initRequestManager(() => login(email, password), onInvalidToken, onNetworkError, true);

    // Need to save new login token
    await updateLocalStorageOnLogin({ webLogin, ...data });

    logLogin(true, 'password', webLogin.seriesId);

    return data;
}

/**
 * Wrapper for `initRequestManager` function that uses a SSO id token. Will store web login details in local storage.
 * @param {string} provider SSO provider.
 * @param {string} idToken SSO ID token.
 * @param {string} refreshToken SSO refresh token.
 * @param {*} onInvalidToken Invoked if a token refresh fails.
 * @param {*} onNetworkError Invoked if token cannot be refreshed due to network error.
 * @returns Account data
 */
export const initRequestManagerSSO = async (provider, idToken, refreshToken, onInvalidToken, onNetworkError) => {
    const { webLogin, ...data } = await initRequestManager(() => ssoLogin(provider, idToken, refreshToken), onInvalidToken, onNetworkError, true);

    // Need to save new login token
    await updateLocalStorageOnLogin({ webLogin, ...data });

    logLogin(true, 'sso', webLogin.seriesId);

    return data;
}

/**
 * Wrapper for `initRequestManager` function that retrieves web login details from local storage.
 * @param {Function} onInvalidToken Invoked if a token refresh fails.
 * @param {Function} onNetworkError Invoked if token cannot be refreshed due to network error.
 * @returns Account data
 */
export const initRequestManagerWebLogin = async (onInvalidToken, onNetworkError) => {
    try {   
        const runFn = isIos ? runFunctionUsingLoginTokenFromNativeLayer : runFunctionUsingLoginTokenInLocalStorage;
        const updateFn = isIos ? updateNativeLayerOnRefreshToken : updateLocalStorageOnRefreshToken;

        const data = await initRequestManager(
            () => runFn(
                refreshTokenAndGetAccountData,
                // Put all data in local storage
                // Do not put any tokens in though
                ({ authToken, managementToken, ...data }) => updateFn(data)
            ),
            onInvalidToken,
            onNetworkError,
            true
        );

        logLogin(true, 'loginToken', data.webLogin.seriesId);

        return data;
    } catch (error) {
        await clearLocalStorage();
        throw error;
    }
}

/**
 * Fetch data for demo account. Obviously does not store details in local storage.
 * @param {string|bool} region AWS region, or boolean true to default to nearest region.
 * @param {*} onInvalidToken Invoked if a token refresh fails.
 * @param {Function} onNetworkError Invoked if token cannot be refreshed due to network error.
 * @returns Account data
 */
export const initRequestManagerDemo = async (region, onInvalidToken, onNetworkError) => {
    const { webLogin, ...data } = await initRequestManager(() => loginWithDemoAccount(region), onInvalidToken, onNetworkError, false);

    // Save loginToken in state which will be used to refresh authToken
    state.webLogin = webLogin;

    logLogin(true, 'demo', webLogin.seriesId);

    return data;
}

/**
 * Fetch account data (either through email/password login or refresh token) and store relevant details.
 * Also sets up first timeout to refresh token.
 * @param {Function} f Async function which returns account data.
 * @param {Function} onInvalidToken Invoked if a token refresh fails.
 * @param {Function} onNetworkError Invoked if token cannot be refreshed due to network error.
 * @param {Boolean} updateLocalStorage Whether to store web login details in local storage.
 * @returns Account data
 */
const initRequestManager = async (f, onInvalidToken, onNetworkError, updateLocalStorage) => {
    // Clear state in case some old details are still in there
    resetState();

    // Fetch account data
    const { authToken, managementToken, ...data } = await f();

    // Update state
    state.onInvalidToken = onInvalidToken;
    state.onNetworkError = onNetworkError;  
    state.updateLocalStorage = updateLocalStorage;

    // Update store
    updateDetails(data.uid, data.region, data.installerid, data.accountType, data.distributorId);
    updateTokens(authToken, managementToken);
    state.lastAuthTokenUpdate = new Date();

    // Run deferred logging that depends on getUid() being set
    if (data._afterStoreInit) {
        data._afterStoreInit();
        delete data._afterStoreInit;
    }

    // Set up timeout to refresh token
    state.authTokenRefreshTimeout = setTimeout(updateLocalStorage ? refreshTokensFromLocalStorage : refreshTokensFromState, REFRESH_TOKENS_TIMEOUT);

    // For mobile apps, check if tokens might be stale due to backgrounding and proactively refresh
    if (isMobileApp && updateLocalStorage) {
        try {
            const loginDetails = getLoginDetailsFromLocalStorage();
            if (loginDetails && loginDetails.tokenTimestamp) {
                const tokenAge = new Date().getTime() - loginDetails.tokenTimestamp;
                if (tokenAge > 600000) { // 10 minutes - tokens might be stale
                    // Proactively refresh tokens instead of waiting for failure
                    setTimeout(refreshTokensFromLocalStorage, 1000);
                }
            }
        } catch (error) {
            // If localStorage access fails, let normal flow handle it
        }
    }

    return data;
}

/**
 * Returns time that auth (and management) token(s) were last updated.
 * @returns {Date}
 */
export const getTimeTokensLastUpdated = () => state.lastAuthTokenUpdate;

export const refreshTokensIfStale = async (maxTokenAgeMs = 480000) => {
    if (
        state.lastAuthTokenUpdate &&
        new Date().getTime() - state.lastAuthTokenUpdate.getTime() <=
            maxTokenAgeMs
    ) {
        return;
    }

    if (state.updateLocalStorage) {
        await refreshTokensFromLocalStorage();
        return;
    }

    if (state.webLogin) {
        await refreshTokensFromState();
        return;
    }

    try {
        const loginDetails = getLoginDetailsFromLocalStorage();

        if (loginDetails?.loginToken) {
            await refreshTokensFromLocalStorage();
        }
    } catch {
        // Normal request handling will deal with missing or corrupted login data.
    }
};

setRefreshTokensIfStaleBeforePrivateRequest(refreshTokensIfStale);


// On logout
export const logout = async () => {
    // Delete series
    try {
        // If user clicks log out, then browser back button, then log out again,
        // getLoginDetailsFromLocalStorage() would throw error (as details have been cleared)
        let loginDetails = state.webLogin;
        if (state.updateLocalStorage) {
            if (window?.webkit?.messageHandlers?.nativeStorage) {
                const nativeResponse = await window.webkit.messageHandlers.nativeStorage.postMessage({
                    action: 'getWebLogin'
                });
                loginDetails = JSON.parse(nativeResponse);
            } else {
                loginDetails = getLoginDetailsFromLocalStorage();
            }
        }

        const { loginToken, seriesId, uid } = loginDetails;

        if (state.updateLocalStorage) {
            await clearLocalStorage();
        }

        await logoutAPI(
            loginToken,
            seriesId,
            uid,
            getAuthenticatorForRegion(getRegion())
        );
    } catch (error) {
        console.error(error);
    }

    // Clear state
    resetState();
}



// Refreshing token
// Token expires after 20 minutes
// We refresh it every 10 minutes
const refreshTokensFromState = async () => runTokenRefresh(refreshTokensFromStateNow);

const refreshTokensFromStateNow = async () => {
    try {
        // If this function was called by something other than timeout, then we no longer need the timeout
        clearRefreshTokenTimeout();

        const [authToken, managementToken, newWebLogin] = await refreshToken(
            state.webLogin.loginToken,
            state.webLogin.seriesId,
            state.webLogin.uid,
            getAuthenticatorForRegion(getRegion()),
            state.webLogin.ssoRefreshToken
        );

        // Store new login token
        state.webLogin.loginToken = newWebLogin.loginToken;
        state.webLogin.ssoRefreshToken = newWebLogin.ssoRefreshToken;

        handleNewTokens(authToken, managementToken, state.webLogin.seriesId);
    } catch (error) {
        onRefreshTokenFail(error, false, state.webLogin.seriesId);
    }
}

// Try to parse webLogin in local storage and use it to fetch new token(s)
const refreshTokensFromLocalStorage = async () => runTokenRefresh(refreshTokensFromLocalStorageNow);

const refreshTokensFromLocalStorageNow = async () => {
    
    // Get series id for logs
    let seriesId;
    try {
        seriesId = JSON.parse(localStorage.webLogin).seriesId;
    } catch (error) {
        console.error(error);
        seriesId = 'ERROR PARSING LOCAL STORAGE';
    }
    
    try {
        clearRefreshTokenTimeout();

        const runFn = isIos ? runFunctionUsingLoginTokenFromNativeLayer : runFunctionUsingLoginTokenInLocalStorage;
        const updateFn = isIos ? updateNativeLayerOnRefreshToken : updateLocalStorageOnRefreshToken;

        const [authToken, managementToken, { seriesId }] = await runFn(refreshToken, ([,,webLogin]) => updateFn({ webLogin }));

        handleNewTokens(authToken, managementToken, seriesId);
        
    } catch (error) {
        onRefreshTokenFail(error, true, seriesId);
    }
};

const runTokenRefresh = refreshTokens => {
    if (!state.authTokenRefreshPromise) {
        state.authTokenRefreshPromise = Promise.resolve()
            .then(refreshTokens)
            .finally(() => {
                state.authTokenRefreshPromise = null;
            });
    }

    return state.authTokenRefreshPromise;
};

const runFunctionUsingLoginTokenFromNativeLayer = async (f, updateFn) => {
    if (!window?.webkit?.messageHandlers?.nativeStorage) {
        return runFunctionUsingLoginTokenInLocalStorage(f, updateFn);
    }

    // We're using an app version that supports native storage access
    // It might be the first time the user has used the app since updating
    // In that case, we need to use webLogin from local storage at this stage
    let nativeResponse;
    let source = "local";
    if (localStorage.webLogin) {
        nativeResponse = localStorage.webLogin;
        // Now get rid of it
        localStorage.removeItem('webLogin');
    } else {
        nativeResponse = await window.webkit.messageHandlers.nativeStorage.postMessage({
            action: 'getWebLogin',
        });
        source = "native";
    }

    const { loginToken, seriesId, uid, ssoRefreshToken } = JSON.parse(nativeResponse);
    let authenticator;

    // Try to get authenticator from local storage
    if (localStorage.loginResponse) {
        try {
            authenticator = JSON.parse(
                localStorage.loginResponse
            ).authenticator;
        } catch (error) {}
    }

    const response = await f(
        loginToken,
        seriesId,
        uid,
        authenticator,
        ssoRefreshToken,
        source
    );

    await updateFn(response);

    return response;
}

// This function will wait until local storage is unlocked, called the specified function f using the login token in local storage, and then update the login token in local storage using `updateLocalStorage`
const runFunctionUsingLoginTokenInLocalStorage = async (f, updateLocalStorage) => {

    /*
        If multiple tabs try to refresh token at same time, the following will happen:
            1. Both tabs read same webLogin from local storage and use it to send request
            2. First tab is successful and login token is updated
            3. Second tab will be unsuccessful as login token has expired
            4. Second tab logs out and clears local storage
            5. First tab will log out as local storage has been cleared

        To avoid this, we put a flag in local storage when a tab is refreshing the token.
        Other tabs must wait for flag to clear before trying to update their own token.

        This flag is called `refreshingTokenUntil` and stores a timestamp of when other tabs are
        free to start using webLogin again. We use time rather than a boolean in case the tab
        that placed the lock gets closed whilst refreshing the token, which would mean the flag
        never gets cleared.

        If more than two tabs are trying to refresh token at same time, then later tabs will have
        to wait for multiple timeouts before being able to access local storage. I've set the
        max attempts before a tab gives up to 5.
    */
    const FLAG = 'refreshingTokenUntil';

    // Promise does not resolve until local storage unlocked
    // Rejects if max attempts reached
    const waitForLocalStorage = () => new Promise((res, rej) => {
        
        // Recursively calls itself until local storage unlocked or attempts limit reached
        const wait = (attempts = 0) => {
            if (localStorage[FLAG]) {
                const refreshingTokenUntil = parseInt(localStorage[FLAG]);
                if (!isNaN(refreshingTokenUntil)) {
                    const currentTime = new Date().getTime();
                    if (currentTime < refreshingTokenUntil) {
                        if (attempts < 5) {
                            // Add random delay to timeout to reduce chance multiple tabs access local storage at same time
                            const randomDelay = Math.floor(Math.random() * 500);
                            setTimeout(() => wait(attempts + 1), refreshingTokenUntil - currentTime + randomDelay);
                            return;
                        } else {
                            // Max attempts exceeded
                            rej();
                            return;
                        }
                    }
                }
            }
            res();
        }

        wait();        
    });

    // Wait for local storage to be unlocked
    try {
        await waitForLocalStorage();
    } catch {
        throw new Error('Max handleRefreshToken attempts exceeded: login token is still locked');
    }

    // Lock local storage
    // Allow 2 seconds for request to have finished
    localStorage[FLAG] = new Date().getTime() + 2000;

    // Get login token from local storage
    const { loginToken: currentLoginToken, seriesId, uid, authenticator, ssoRefreshToken: currentSSORefreshToken } =
        getLoginDetailsFromLocalStorage();

    const response = await f(
        currentLoginToken,
        seriesId,
        uid,
        authenticator,
        currentSSORefreshToken,
        "local"
    );

    // Update local storage with new login token using provided function
    updateLocalStorage(response);
    
    // Release lock
    localStorage.removeItem(FLAG);

    return response;
}


// Clears timeout if not done already
const clearRefreshTokenTimeout = () => {
    if (state.authTokenRefreshTimeout) {
        clearTimeout(state.authTokenRefreshTimeout);
        state.authTokenRefreshTimeout = null;
    }
}

const handleNewTokens = (newAuthToken, newManagementToken, seriesId) => {
    // Update stored tokens
    updateTokens(newAuthToken, newManagementToken);
    state.lastAuthTokenUpdate = new Date();
    state.authTokenRefreshTimeout = setTimeout(state.updateLocalStorage ? refreshTokensFromLocalStorage : refreshTokensFromState, REFRESH_TOKENS_TIMEOUT);
    
    // Log to Athena
    logLogin(false, 'loginToken', seriesId);
}

/**
 * Invokes relevant callback when a token refresh fails. If failure wasn't a simple network
 * request, this also clears state.
 * @param {Error} error Error thrown when token refresh failed.
 * @param {boolean} usingLocalStorage Are login token details stored in local storage as opposed to state?
 * @param {string} seriesId Series id of login token.
 * @param {boolean} skipNetworkErrorRetry Skip retry for network errors (to prevent infinite retry).
 */
const onRefreshTokenFail = async (error, usingLocalStorage, seriesId, skipNetworkErrorRetry = false) => {
    if (error) {
        // Will not be an error if another tab logged out
        // In that case, this function is still used for convenience
        console.error(error); 
    }

    if (error?.message === 'Network Error') {
        // For mobile apps, retry network errors once before giving up
        if (isMobileApp && !skipNetworkErrorRetry && usingLocalStorage) {
            console.warn('Mobile app network error during token refresh, retrying in 2 seconds...');
            setTimeout(() => {
                refreshTokensFromLocalStorage().catch((retryError) => {
                    // If retry fails, proceed with normal logout flow
                    onRefreshTokenFail(retryError, usingLocalStorage, seriesId, true);
                });
            }, 2000);
            return;
        }
        state.onNetworkError?.();
    } else {
        if (usingLocalStorage) {
            await clearLocalStorage();
        }

        if (error) {
            // Attempt to log to Athena if authToken still valid
            try {
                logForcedLogout(seriesId, error.message);
            } catch (error) {
                console.error(error);
            }
        }

        state.onInvalidToken?.();
    }
    resetState();
}



// Refresh token when tab comes back from background as timeout may have been throttled

// Check whether token needs refreshing when tab becomes active again
const onTabVisibilityChange = async () => {
    if (document.visibilityState === 'visible') {
        // Handle mobile app native wrapper refresh - state is lost but tokens might be stale
        if (isMobileApp && !state.lastAuthTokenUpdate) {
            try {
                const loginDetails = getLoginDetailsFromLocalStorage();
                if (loginDetails && loginDetails.loginToken) {
                    // Force immediate token refresh on mobile app resume after native refresh
                    const randomDelay = Math.floor(Math.random() * 500);
                    setTimeout(refreshTokensFromLocalStorage, randomDelay);
                }
            } catch (error) {
                // If localStorage is corrupted, let normal flow handle it
            }
            return;
        }

        /*
                Check if token is older than 8 mins.
                Use 8 mins rather than usual 10 to be on safe side.
                Worst case scenario using 10 mins:
                    - Tab put in background immediately after token refresh
                    - Tab resumed 9:59 later
                    - This code wouldn't trigger and timeout could still have 10 mins to run
                    - May be a brief period where token has expired
            */
        if (state.lastAuthTokenUpdate && new Date().getTime() - state.lastAuthTokenUpdate.getTime() > 480000) {
            // Add random timeout to reduce chance multiple tabs access local storage at same time
            // If that were to happen, local storage may not be locked yet and they could both try to refresh token at same time
            const randomDelay = Math.floor(Math.random() * 500);
            setTimeout(state.updateLocalStorage ? refreshTokensFromLocalStorage : refreshTokensFromState, randomDelay);
        }
    }
};

document.addEventListener('visibilitychange', onTabVisibilityChange);



// If another tab logs out, immediately log this one out too
// Otherwise this would happen on next token refresh which would of course fail
window.addEventListener('storage', event => {
    if (state.updateLocalStorage && (event.key === 'webLogin' || event.key === null) && !event.storageArea.webLogin) {
        console.warn('Another tab cleared local storage')
        onRefreshTokenFail(null, true);
    }
});
